import { IContextOptionNormalized } from "../context-options/interfaces/context-options-normalized"
import { AppMainContextType, AppSubContextType } from "../types/app-context"

const contextTypes = {
  USER: "user",
  ORG: "org",
  CUSTOMER: "customer",
  SUPPLIER: "supplier",
  WORKSPACE: "workspace",
  MEMBERSHIP: "member",
} as const

const contextOptionTypes = {
  USER: "user",
  ORGANISATION: "organisation",
  CUSTOMER: "customer",
  SUPPLIER: "supplier",
  WORKSPACE: "workspace",
  MEMBERSHIP: "membership",
} as const

const contextOptionTypeToContextType = {
  [contextOptionTypes.USER]: contextTypes.USER,
  [contextOptionTypes.ORGANISATION]: contextTypes.ORG,
  [contextOptionTypes.CUSTOMER]: contextTypes.CUSTOMER,
  [contextOptionTypes.SUPPLIER]: contextTypes.SUPPLIER,
  [contextOptionTypes.WORKSPACE]: contextTypes.WORKSPACE,
  [contextOptionTypes.MEMBERSHIP]: contextTypes.MEMBERSHIP,
} as const

export const isOrgContextType = (type: string): type is typeof contextTypes.ORG => {
  return type === contextTypes.ORG
}

export const isUserContextType = (type: string): type is typeof contextTypes.USER => {
  return type === contextTypes.USER
}

export const isMainContext = (context: AppMainContextType | AppSubContextType): context is AppMainContextType => {
  return isUserContextType(context.type) || isOrgContextType(context.type)
}

export const isSubContext = (context: AppMainContextType | AppSubContextType): context is AppSubContextType => {
  return (
    context.type === contextTypes.CUSTOMER ||
    context.type === contextTypes.SUPPLIER ||
    context.type === contextTypes.WORKSPACE ||
    context.type === contextTypes.MEMBERSHIP
  )
}

export class ContextOption {
  static isUserContextOption(contextOption: IContextOptionNormalized): boolean {
    return contextOption.type === contextOptionTypes.USER
  }

  static isOrganisationContextOption(contextOption: IContextOptionNormalized): boolean {
    return contextOption.type === contextOptionTypes.ORGANISATION
  }

  static isMainContextOption(contextOption: IContextOptionNormalized): boolean {
    return ContextOption.isUserContextOption(contextOption) || ContextOption.isOrganisationContextOption(contextOption)
  }

  static isSubContextOption(contextOption: IContextOptionNormalized): boolean {
    return (
      contextOption.type === contextOptionTypes.CUSTOMER ||
      contextOption.type === contextOptionTypes.SUPPLIER ||
      contextOption.type === contextOptionTypes.WORKSPACE ||
      contextOption.type === contextOptionTypes.MEMBERSHIP
    )
  }

  static toMainContext(contextOption: IContextOptionNormalized): AppMainContextType {
    if (!ContextOption.isMainContextOption(contextOption)) {
      throw new Error("Invalid context option type. Must be 'user' or 'organisation'.")
    }

    return {
      ...contextOption,
      type: contextOptionTypeToContextType[contextOption.type] as AppMainContextType["type"],
    }
  }

  static toSubContext(contextOption: IContextOptionNormalized): AppSubContextType {
    if (!ContextOption.isSubContextOption(contextOption)) {
      throw new Error("Invalid context option type. Must be a sub-context type.")
    }

    const type = contextOptionTypeToContextType[contextOption.type] as AppSubContextType["type"]
    const parentContextId = contextOption.orgId

    return {
      ...contextOption,
      type,
      parentContextId,
    }
  }
}
